<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email, requiredIf } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { CoDeptor } from '@/types/CoDeptor'
import SsnInput from '../../Shared/SSNInput.vue'

const props = defineProps<{ disable?: boolean }>()
const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const rules = computed(() => {

  return {
    umsokn: {
      userIsDeptor: { required },
      deptorName: {
        required: requiredIf(() => umsokn.value?.userIsDeptor === false),
      },
      deptorSSN: {
        required: requiredIf(() => umsokn.value?.userIsDeptor === false),
      },
      deptorPhone: {
        required: requiredIf(() => umsokn.value?.userIsDeptor === false),
      },
      deptorEmail: {
        required: requiredIf(() => umsokn.value?.userIsDeptor === false),
        email,
      }
    },
  }
})

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  async validate() {
    const valid = await v$.value.$validate()
    return valid
  },
})

onLoaded(() => {
  if (!umsokn.value) return
  if (!Array.isArray(umsokn.value.coDeptors) || umsokn.value.coDeptors.length === 0) {
    umsokn.value.coDeptors = [{ name: '', ssn: '', phone: '', email: '' } as CoDeptor]
  }
  umsokn.value.deptorName ||= ''
  umsokn.value.deptorSSN ||= ''
  umsokn.value.deptorPhone ||= ''
  umsokn.value.deptorEmail ||= ''
})

function addCoDeptor() {
  umsokn.value?.coDeptors.push({ name: '', ssn: '', phone: '', email: '' })
}

function removeCoDeptor(i: number) {
  umsokn.value?.coDeptors.splice(i, 1)
  if (umsokn.value && umsokn.value.coDeptors.length === 0) {
    umsokn.value.coDeptors = [{ name: '', ssn: '', phone: '', email: '' }]
  }
}
</script>

<template>
  <div v-if="umsokn">
    <!-- Radio -->
    <v-col>
      <v-radio-group
        v-model="umsokn.userIsDeptor"
        :readonly="props.disable"
        hide-details
        :error="v$.umsokn.userIsDeptor.$error && v$.umsokn.userIsDeptor.$dirty"
        @blur="v$.umsokn.userIsDeptor.$touch()"
      >
        <v-radio :label="t('lan_vedflutning_step3_sjalfur_skuldari')" :value="true" />
        <v-radio :label="t('lan_vedflutning_step3_fyrir_hond')" :value="false" />
      </v-radio-group>
      <div v-if="v$.umsokn.userIsDeptor.$dirty && v$.umsokn.userIsDeptor.$error" class="error__text">
        {{ t('lan_vedflutning_step3_sjalfur_skuldari_error') }}
      </div>
    </v-col>

    <!-- Deptor info -->
    <v-col v-if="umsokn.userIsDeptor === false" style="margin: 20px 0;">
      <div class="forms__header">{{ t('lan_vedflutning_step3_header') }}</div>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.deptorName"
            :readonly="props.disable"
            :label="t('lan_vedflutning_step3_samskuldari_nafn')"
            variant="outlined"
            density="comfortable"
            :error="v$.umsokn.deptorName.$dirty && v$.umsokn.deptorName.$error"
            :error-messages="v$.umsokn.deptorName.$dirty && v$.umsokn.deptorName.$error
              ? [t('lan_vedflutning_step3_samskuldari_nafn_error')]
              : []"
            @blur="v$.umsokn.deptorName.$touch()"
          />
        </v-col>
        <v-col cols="12" md="6">
          <SsnInput
            v-model="umsokn.deptorSSN"
            :readonly="props.disable"
            :label="t('lan_vedflutning_step3_samskuldari_kennitala')"
            placeholder="######-####"
            variant="outlined"
            :error="v$.umsokn.deptorSSN.$dirty && v$.umsokn.deptorSSN.$error"
            :error-messages="v$.umsokn.deptorSSN.$dirty && v$.umsokn.deptorSSN.$error
              ? [t('lan_vedflutning_step3_samskuldari_kennitala_error')]
              : []"
            @blur="v$.umsokn.deptorSSN.$touch()"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.deptorPhone"
            :readonly="props.disable"
            :label="t('lan_vedflutning_step3_samskuldari_simi')"
            variant="outlined"
            density="comfortable"
            type="tel"
            :error="v$.umsokn.deptorPhone.$dirty && v$.umsokn.deptorPhone.$error"
            :error-messages="v$.umsokn.deptorPhone.$dirty && v$.umsokn.deptorPhone.$error
              ? [t('lan_vedflutning_step3_samskuldari_simi_error')]
              : []"
            @blur="v$.umsokn.deptorPhone.$touch()"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.deptorEmail"
            :readonly="props.disable"
            :label="t('lan_vedflutning_step3_samskuldari_email')"
            variant="outlined"
            density="comfortable"
            type="email"
            :error="v$.umsokn.deptorEmail.$dirty && v$.umsokn.deptorEmail.$error"
          :error-messages="[
            v$.umsokn.deptorEmail.required?.$invalid && v$.umsokn.deptorEmail.$dirty
              ? t('lan_vedflutning_step3_samskuldari_email_error')
              : '',
            v$.umsokn.deptorEmail.email?.$invalid && v$.umsokn.deptorEmail.$dirty
              ? t('lan_vedflutning_step3_samskuldari_email_invalid')
              : ''
          ].filter(Boolean)"
            @blur="v$.umsokn.deptorEmail.$touch()"
          />
        </v-col>
      </v-row>
    </v-col>

    <!-- Co-debtors -->
    <v-col>
      <div class="forms__header">{{ t('lan_vedflutning_step3_samskuldari_header') }}</div>

      <transition-group name="list">
        <div v-for="(deptor, i) in umsokn.coDeptors" :key="'coDeptor_' + i">
          <div v-if="i > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button v-if="!props.disable" class="forms__warning__list__wrapper" @click="removeCoDeptor(i)">
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" />
            </button>
          </div>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="deptor.name"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step3_samskuldari_nafn')"
                variant="outlined"
                density="comfortable"
              />
            </v-col>
            <v-col cols="12" md="6">
              <SsnInput
                v-model="deptor.ssn"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step3_samskuldari_kennitala')"
                placeholder="######-####"
                variant="outlined"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="deptor.phone"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step3_samskuldari_simi')"
                variant="outlined"
                density="comfortable"
                type="tel"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="deptor.email"
                :readonly="props.disable"
                :label="t('lan_vedflutning_step3_samskuldari_email')"
                variant="outlined"
                density="comfortable"
                type="email"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
         <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addCoDeptor"
        >
          {{ $t('ororku_umsokn_step3_add') }}
        </v-btn>
      </div>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.forms__header {
  margin: 20px 0;
}
</style>
